import { Component, OnInit, Input } from '@angular/core';
import { ModalController } from '@ionic/angular';

export interface Committee {
  id: number | string;
  name: string;
}

@Component({
  selector: 'app-committee-selection-modal',
  standalone: false,
  templateUrl: './committee-selection-modal.component.html',
  styleUrls: ['./committee-selection-modal.component.scss'],
})
export class CommitteeSelectionModalComponent implements OnInit {

  @Input() allCommittees: Committee[] = [];
  @Input() selectedCommitteeIds: string[] = [];

  // Internal state
  searchTerm: string = '';
  selectedIds: Set<string> = new Set();
  filteredCommittees: Committee[] = [];

  constructor(private modalController: ModalController) { }

  ngOnInit() {
    // Initialize selected IDs set
    this.selectedIds = new Set(this.selectedCommitteeIds.map(id => String(id)));

    // Initialize filtered committees
    this.filteredCommittees = [...this.allCommittees];

    console.log('Committee Modal initialized with:', {
      total: this.allCommittees.length,
      selected: this.selectedIds.size
    });
  }

  /**
   * Filter committees based on search term
   */
  filterCommittees(): void {
    const term = this.searchTerm.toLowerCase().trim();

    if (!term) {
      this.filteredCommittees = [...this.allCommittees];
      return;
    }

    this.filteredCommittees = this.allCommittees.filter(committee =>
      committee.name.toLowerCase().includes(term)
    );
  }

  /**
   * Get selected committees
   */
  getSelectedCommittees(): Committee[] {
    return this.allCommittees.filter(committee =>
      this.selectedIds.has(String(committee.id))
    );
  }

  /**
   * Get available (non-selected) committees from filtered list
   */
  getAvailableCommittees(): Committee[] {
    return this.filteredCommittees.filter(committee =>
      !this.selectedIds.has(String(committee.id))
    );
  }

  /**
   * Toggle committee selection
   */
  toggleCommittee(committee: Committee, event?: any): void {
    const committeeId = String(committee.id);

    if (this.selectedIds.has(committeeId)) {
      this.selectedIds.delete(committeeId);
    } else {
      this.selectedIds.add(committeeId);
    }
  }

  /**
   * Check if committee is selected
   */
  isSelected(committee: Committee): boolean {
    return this.selectedIds.has(String(committee.id));
  }

  /**
   * Select all filtered committees
   */
  selectAll(): void {
    this.filteredCommittees.forEach(committee => {
      this.selectedIds.add(String(committee.id));
    });
  }

  /**
   * Clear all selections
   */
  clearAll(): void {
    this.selectedIds.clear();
  }

  /**
   * Highlight matching text in committee name
   */
  highlightMatch(name: string): string {
    if (!this.searchTerm) {
      return name;
    }

    const term = this.searchTerm.trim();
    const regex = new RegExp(`(${term})`, 'gi');
    return name.replace(regex, '<mark>$1</mark>');
  }

  /**
   * Confirm selection and close modal
   */
  confirm(): void {
    const selectedIds = Array.from(this.selectedIds);
    this.modalController.dismiss({
      selectedIds: selectedIds
    }, 'confirm');
  }

  /**
   * Cancel and close modal
   */
  dismiss(): void {
    this.modalController.dismiss(null, 'cancel');
  }

  /**
   * Get count of selected committees
   */
  get selectedCount(): number {
    return this.selectedIds.size;
  }

  /**
   * Get count of available (filtered) committees
   */
  get availableCount(): number {
    return this.getAvailableCommittees().length;
  }

  /**
   * Check if there are any filtered results
   */
  get hasFilteredResults(): boolean {
    return this.filteredCommittees.length > 0;
  }

  /**
   * Check if search returned no results
   */
  get noSearchResults(): boolean {
    return this.searchTerm.length > 0 && this.filteredCommittees.length === 0;
  }

}
